import {
    colorAttentionBlue10,
    colorAttentionRed50,
    colorFunctionalGray10,
    colorFunctionalGray30,
    colorLayoutBorder,
    colorMiscBlue20,
    colorMiscBlue70,
    colorMiscGray90,
    fontSizeM,
    fontSizeS,
    IconX,
    spacingS,
    spacingSm,
    spacingXs,
    spacingXxs,
} from "@rhumbix/rmbx_design_system_web"
import { forwardRef, useImperativeHandle, useState, useRef, KeyboardEvent, useEffect, useMemo } from "react"
import styled from "styled-components"
import { JsonPointer as jsonpointer } from "json-ptr"

import { cleanCommaSeparatedEmails, isValidEmailString, cleanEmailAddress } from "../../../common/ts-utils"
import { ICellEditorParams } from "ag-grid-community"

// Styled Components
const EditorContainer = styled.div`
    width: 100%;
    min-width: 300px;
    padding: ${spacingXs};
    background: white;
    border: 1px solid ${colorLayoutBorder};
    border-radius: ${spacingXs};
    position: relative;
`

const TagsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacingXs};
    align-items: center;
    min-height: 32px;
    padding: ${spacingXs};
`

const EmailTag = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${colorAttentionBlue10};
    border-radius: ${spacingXs};
    height: 30px;
    max-width: 100%;
    padding: ${spacingXxs} ${spacingXs} ${spacingXxs} ${spacingS};
    font-size: ${fontSizeM};
    color: ${colorMiscBlue70};

    &:hover {
        background-color: ${colorMiscBlue20};
    }
`

const EmailTagText = styled.span`
    flex: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: ${spacingS};
    text-align: left;
`

const RemoveButton = styled.button`
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    min-height: 30px;
    max-width: 30px;
    border: none;
    background: transparent;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    border-radius: ${spacingXxs};
`

const EmailInput = styled.input`
    flex: 1;
    min-width: 150px;
    border: none;
    outline: none;
    font-size: ${fontSizeM};
    padding: ${spacingXs};
    background: transparent;

    &::placeholder {
        color: ${colorFunctionalGray30};
    }
`

const ErrorMessage = styled.div`
    color: ${colorAttentionRed50};
    font-size: ${fontSizeS};
    margin-top: ${spacingXs};
    padding: 0 ${spacingXs};
`

const SuggestionsContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid ${colorLayoutBorder};
    border-top: none;
    border-radius: 0 0 ${spacingXs} ${spacingXs};
    max-height: 150px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 ${spacingXs} 6px rgba(0, 0, 0, 0.1);
`

const SuggestionItem = styled.div<{ isSelected: boolean }>`
    padding: ${spacingS} ${spacingSm};
    cursor: pointer;
    font-size: ${fontSizeS};
    color: ${colorMiscGray90};
    transition: background-color 0.2s;
    background-color: ${props => (props.isSelected ? colorFunctionalGray10 : "transparent")};

    &:hover {
        background-color: ${colorFunctionalGray10};
    }

    &:first-child {
        border-top: 1px solid ${colorLayoutBorder};
    }
`

const WARNING_MESSAGE_DURATION = 3000 // 3 seconds

const EmailTagCellEditor = forwardRef((props: ICellEditorParams, ref) => {
    // Parse the comma-separated email string into an array
    const parseEmails = (emailString: string): string[] => {
        if (!emailString || typeof emailString !== "string") return []

        return cleanCommaSeparatedEmails(emailString)
    }

    const [emails, setEmails] = useState<string[]>(parseEmails(props.value))
    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")
    const [suggestions, setSuggestions] = useState<string[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
    const inputRef = useRef<HTMLInputElement>(null)
    const suggestionsRef = useRef<HTMLDivElement>(null)
    const currentSuggestionsRef = useRef<string[]>([])
    const currentSelectedIndexRef = useRef<number>(-1)
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Keep refs in sync with state
    useEffect(() => {
        currentSuggestionsRef.current = suggestions
    }, [suggestions])

    useEffect(() => {
        currentSelectedIndexRef.current = selectedSuggestionIndex
    }, [selectedSuggestionIndex])

    // Helper function to show temporary error messages
    const showTemporaryError = (message: string) => {
        // Clear any existing timeout
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current)
        }

        setError(message)

        // Set new timeout and store its reference
        errorTimeoutRef.current = setTimeout(() => {
            setError("")
            errorTimeoutRef.current = null
        }, WARNING_MESSAGE_DURATION)
    }

    // Collect all unique emails from all rows (recomputed only when grid/field changes)
    const allEmails = useMemo(() => {
        const uniqueEmailsFromGrid = new Set<string>()

        props.api.forEachNode((node: any) => {
            const fieldName = props?.colDef?.field
            if (!fieldName) return

            let rowEmails = ""

            // if the field name starts with a slash, try finding it using jsonpointer
            if (fieldName.startsWith("/")) {
                rowEmails = (jsonpointer.get(node?.data ?? {}, fieldName) ?? "") as string
            }

            // if the field doesn't have a slash or jsonpointer doesn't find it, try the path as-is
            if (!rowEmails) {
                rowEmails = node?.data?.[fieldName] ?? ""
            }

            if (rowEmails && typeof rowEmails === "string") {
                cleanCommaSeparatedEmails(rowEmails).forEach((email: string) => {
                    const trimmed = email.trim()
                    if (trimmed) {
                        uniqueEmailsFromGrid.add(trimmed)
                    }
                })
            }
        })

        return uniqueEmailsFromGrid
    }, [props.api, props?.colDef?.field])

    // Filter suggestions per keystroke (much more efficient)
    useEffect(() => {
        if (inputValue.length === 0) {
            setSuggestions([])
            setShowSuggestions(false)
            return
        }

        const filtered = Array.from(allEmails)
            .filter(email => email.toLowerCase().includes(inputValue.toLowerCase()) && !emails.includes(email))
            .slice(0, 5) // Limit to 5 suggestions

        setSuggestions(filtered)
        setShowSuggestions(filtered.length > 0)
    }, [inputValue, emails, allEmails])

    // Focus the input when the editor opens
    useImperativeHandle(ref, () => {
        return {
            getValue: () => {
                // Add any remaining input as an email before returning
                if (inputValue.trim()) {
                    const finalEmails = [...emails]
                    const cleanedEmail = cleanEmailAddress(inputValue)
                    if (!emails.includes(cleanedEmail)) {
                        finalEmails.push(cleanedEmail)
                    }
                    return finalEmails.join(", ")
                }
                // Convert the array back to a comma-separated string
                return emails.join(", ")
            },
            afterGuiAttached: () => {
                // Focus the input after the component is attached to the DOM
                setTimeout(() => {
                    inputRef.current?.focus()
                }, 0)
            },
        }
    })

    // focus on mount
    useEffect(() => {
        // Small timeout to ensure the DOM is ready
        const timer = setTimeout(() => {
            inputRef.current?.focus()
        }, 10)

        return () => {
            clearTimeout(timer)
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current)
            }
        }
    }, [])

    /**
     * Removing one email at a time from the list
     * @param index
     */
    const removeEmail = (index: number) => {
        const newEmails = emails.filter((_, i) => i !== index)
        setEmails(newEmails)
    }

    /**
     * Adding one email at a time to the list
     * Show a warning if the email appears invalid, but still add it
     * Let the backend do the actual validation
     * @param email
     */
    const addEmail = (email: string) => {
        const cleanedEmail = cleanEmailAddress(email)
        if (!cleanedEmail) return

        // Check for duplicates
        if (emails.includes(cleanedEmail)) {
            showTemporaryError(`"${cleanedEmail}" is already in the list`)
            return
        }

        // Show a warning if email appears invalid, but still add it
        // Backend will do the actual validation
        if (!isValidEmailString(cleanedEmail)) {
            showTemporaryError(`"${cleanedEmail}" may not be a valid email`)
        }

        setEmails(prev => [...prev, cleanedEmail])
        setInputValue("")
    }

    /**
     * Handle paste event, which will set multiple emails at once
     * Show warnings for invalid emails but still add them
     * Let the backend do the actual validation
     * @param e
     */
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData("text")
        const pastedEmails = cleanCommaSeparatedEmails(pastedText)

        const newEmails: string[] = []
        const duplicates: string[] = []
        const invalidEmails: string[] = []

        pastedEmails.forEach(email => {
            const cleanedEmail = cleanEmailAddress(email)
            if (!cleanedEmail) return

            if (emails.includes(cleanedEmail)) {
                duplicates.push(cleanedEmail)
            } else {
                newEmails.push(cleanedEmail)
                // Check for invalid emails but still add them
                if (!isValidEmailString(cleanedEmail)) {
                    invalidEmails.push(cleanedEmail)
                }
            }
        })

        // Add all new emails (including invalid ones for backend validation)
        if (newEmails.length > 0) {
            setEmails(prev => [...prev, ...newEmails])
            setInputValue("")
        }

        // Show appropriate warnings
        if (duplicates.length > 0) {
            showTemporaryError(`Skipped duplicate(s): ${duplicates.join(", ")}`)
        } else if (invalidEmails.length > 0) {
            showTemporaryError(`${invalidEmails.join(", ")} may not be valid`)
        }
    }

    const selectSuggestion = (email: string) => {
        setEmails(prev => [...prev, email])
        setInputValue("")
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
        // Use setTimeout to ensure focus happens after state updates
        setTimeout(() => {
            inputRef.current?.focus()
        }, 0)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        // Check if we have suggestions visible
        if (showSuggestions && suggestions.length > 0) {
            // Navigate suggestions with arrow keys
            if (e.key === "ArrowDown") {
                e.preventDefault()
                setSelectedSuggestionIndex(prev => (prev < suggestions.length - 1 ? prev + 1 : 0))
                return
            } else if (e.key === "ArrowUp") {
                e.preventDefault()
                setSelectedSuggestionIndex(prev => (prev > 0 ? prev - 1 : suggestions.length - 1))
                return
            } else if (e.key === "Enter") {
                e.preventDefault()
                e.stopPropagation()
                const currentIndex = currentSelectedIndexRef.current
                const currentSuggestions = currentSuggestionsRef.current
                // If a suggestion is selected, use it
                if (currentIndex >= 0 && currentIndex < currentSuggestions.length) {
                    const selectedEmail = currentSuggestions[currentIndex]
                    selectSuggestion(selectedEmail)
                } else if (inputValue.trim()) {
                    // Otherwise add the typed value if valid
                    addEmail(inputValue)
                }
                return
            } else if (e.key === "Escape") {
                e.preventDefault()
                setShowSuggestions(false)
                setSelectedSuggestionIndex(-1)
                return
            }
        }

        // Handle keys when no suggestions are shown
        if (e.key === " " || e.key === "," || e.key === ";") {
            e.preventDefault()
            if (inputValue.trim()) {
                addEmail(inputValue)
            }
        } else if (e.key === "Enter") {
            e.preventDefault()
            if (inputValue.trim()) {
                addEmail(inputValue)
            }
        }
        // Remove last email on Backspace if input is empty
        else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
            removeEmail(emails.length - 1)
        }
    }

    return (
        <EditorContainer>
            <TagsContainer>
                {emails.map((email, index) => (
                    <EmailTag key={index}>
                        <EmailTagText>{email}</EmailTagText>
                        <RemoveButton
                            onClick={() => removeEmail(index)}
                            type="button"
                            aria-label={`Remove ${email}`}
                        >
                            <IconX color={colorMiscBlue70} height={14} width={14} />
                        </RemoveButton>
                    </EmailTag>
                ))}
                <EmailInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    onChange={e => {
                        setInputValue(e.target.value)
                        setSelectedSuggestionIndex(-1)
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyDownCapture={e => {
                        // this allows using the arrow keys to navigate suggestions,
                        // and hitting enter to select a suggestion,
                        // if this is removed, hitting enter will close the input without selecting a suggestion
                        if (e.key === "Enter" && showSuggestions && selectedSuggestionIndex >= 0) {
                            e.preventDefault()
                            e.stopPropagation()
                            const currentSuggestions = currentSuggestionsRef.current
                            if (selectedSuggestionIndex < currentSuggestions.length) {
                                selectSuggestion(currentSuggestions[selectedSuggestionIndex])
                            }
                        }
                    }}
                    onPaste={handlePaste}
                    onBlur={e => {
                        // Check if blur is caused by clicking on a suggestion
                        if (suggestionsRef.current?.contains(e.relatedTarget as Node)) {
                            return
                        }
                        // Hide suggestions after a small delay to allow click events
                        setTimeout(() => {
                            setShowSuggestions(false)
                            // Add any remaining email when losing focus
                            const cleanedEmail = cleanEmailAddress(inputValue)
                            if (cleanedEmail) {
                                addEmail(cleanedEmail)
                            }
                        }, 200)
                    }}
                    onFocus={() => {
                        // Re-show suggestions if there are any
                        if (suggestions.length > 0 && inputValue.length > 0) {
                            setShowSuggestions(true)
                        }
                    }}
                    placeholder={emails.length === 0 ? "Type email and press space..." : "Add more..."}
                />
            </TagsContainer>
            {showSuggestions && suggestions.length > 0 && (
                <SuggestionsContainer ref={suggestionsRef}>
                    {suggestions.map((suggestion, index) => (
                        <SuggestionItem
                            key={suggestion}
                            isSelected={index === selectedSuggestionIndex}
                            onMouseDown={e => {
                                e.preventDefault() // Prevent blur
                                selectSuggestion(suggestion)
                            }}
                            onMouseEnter={() => setSelectedSuggestionIndex(index)}
                        >
                            {suggestion}
                        </SuggestionItem>
                    ))}
                </SuggestionsContainer>
            )}
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </EditorContainer>
    )
})

export default EmailTagCellEditor
